import { faCalendar, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { getFiscalDateRangeOptions, mondayOfWeek, rangeFromReportLink } from '@supply-mover-2-0/core';
import { useEffect } from 'react';
import { reportDateRangeStore } from '../../filters/reportDateRangeStore';
import { useFiscalYearStartMonth } from '../../hooks/useFiscalYearStartMonth';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';
import { Dropdown } from '../overlays/Dropdown';
import { Select } from './Select';

export interface DateRangeOption {
	id: string;
	label: string;
	value: {
		startDate: Date | null;
		endDate: Date | null;
	};
}

const getQuarter = (date: Date): number => Math.floor(date.getMonth() / 3);

const weekRange = (weeksBack: number): DateRangeOption['value'] => {
	const monday = mondayOfWeek(new Date());
	const start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7 * weeksBack);
	const end =
		weeksBack === 0
			? new Date(new Date().setHours(23, 59, 59, 999))
			: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6, 23, 59, 59, 999);
	return { startDate: start, endDate: end };
};

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
	// PD-17424 — Monday to Sunday weeks.
	{ id: 'thisWeek', label: 'This week', value: weekRange(0) },
	{ id: 'lastWeek', label: 'Last week', value: weekRange(1) },
	{
		id: 'today',
		label: 'Today',
		value: {
			startDate: new Date(new Date().setHours(0, 0, 0, 0)),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'yesterday',
		label: 'Yesterday',
		value: {
			startDate: new Date(
				new Date(Date.now() - 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(
				new Date(Date.now() - 24 * 60 * 60 * 1000).setHours(23, 59, 59, 999),
			),
		},
	},
	{
		id: 'last7days',
		label: 'Last 7 days',
		value: {
			startDate: new Date(
				new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'last30days',
		label: 'Last 30 days',
		value: {
			startDate: new Date(
				new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'thisMonth',
		label: 'This month',
		value: {
			startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
			endDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth() + 1,
				0,
				23,
				59,
				59,
				999,
			),
		},
	},
	{
		id: 'lastMonth',
		label: 'Last month',
		value: {
			startDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth() - 1,
				1,
			),
			endDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth(),
				0,
				23,
				59,
				59,
				999,
			),
		},
	},
	{
		id: 'thisQuarter',
		label: 'This quarter',
		value: (() => {
			const today = new Date();
			const currentQuarter = getQuarter(today);
			const startMonth = currentQuarter * 3;
			const endMonth = startMonth + 3;
			return {
				startDate: new Date(today.getFullYear(), startMonth, 1),
				endDate:
					endMonth < 12
						? new Date(today.getFullYear(), endMonth, 0, 23, 59, 59, 999)
						: new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999),
			};
		})(),
	},
	{
		id: 'lastQuarter',
		label: 'Last quarter',
		value: (() => {
			const today = new Date();
			const currentQuarter = getQuarter(today);
			const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
			const year =
				currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
			const startMonth = lastQuarter * 3;
			const endMonth = startMonth + 3;
			return {
				startDate: new Date(year, startMonth, 1),
				endDate: new Date(year, endMonth, 0, 23, 59, 59, 999),
			};
		})(),
	},
	{
		id: 'thisYear',
		label: 'This year',
		value: {
			startDate: new Date(new Date().getFullYear(), 0, 1),
			endDate: new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
		},
	},
	{
		id: 'lastYear',
		label: 'Last year',
		value: {
			startDate: new Date(new Date().getFullYear() - 1, 0, 1),
			endDate: new Date(new Date().getFullYear() - 1, 11, 31, 23, 59, 59, 999),
		},
	},
	{
		id: 'custom',
		label: 'Custom',
		value: {
			startDate: null,
			endDate: null,
		},
	},
];

interface DateRangePillSelectorProps {
	selectedValue: string;
	onSelect: (rangeId: string) => void;
	onCustomRangeClick?: () => void;
	currentRange: { label: string; startDate: string; endDate: string };
	customOptions?: Array<{ value: string; label: string }>;
	className?: string;
	pillClassName?: string;
	disabled?: boolean;
	/** Optional label displayed above the pills. Defaults to "Date Range" */
	label?: string;
	/** When true, hides the "Custom" date range option */
	hideCustomOption?: boolean;
	/**
	 * PD-15207 — how the ranges render. The pill row reads well with space to
	 * spare, but on report pages whose filters share the row it crowds them
	 * into the tray; `select` collapses the same choices into one compact
	 * dropdown. Same options, same callbacks, purely presentational.
	 */
	variant?: 'pills' | 'select';
}

/**
 * PD-17424 — the ranges that get a pill of their own, in this order; every
 * other range sits under More so the row never wraps. A page whose own list
 * holds none of these shows its first five instead.
 */
const PRIMARY_RANGE_IDS = ['lastWeek', 'thisWeek', 'lastMonth', 'thisMonth', 'thisYear'];
const PRIMARY_PILL_COUNT = 5;

/** Groups under More, so related ranges sit together between rules. */
const MORE_GROUPS: Array<RegExp> = [
	/^(today|yesterday|last\d+days|last\d+months)$/,
	/^(thisQuarter|lastQuarter|lastYear|twoMonthsAgo|threeMonthsAgo|fourMonthsAgo|fiveMonthsAgo|lifetime)$/,
	/Fiscal/,
	/^custom$/,
];

const moreGroupOf = (id: string): number => {
	const index = MORE_GROUPS.findIndex((pattern) => pattern.test(id));
	return index === -1 ? MORE_GROUPS.length - 1 : index;
};

/** Splits the options into the pill row and the More menu. */
export function splitDateRangeOptions<T extends { id: string }>(
	options: Array<T>,
): { pills: Array<T>; more: Array<T> } {
	const primary = PRIMARY_RANGE_IDS.map((id) => options.find((option) => option.id === id)).filter(
		(option): option is T => option != null,
	);
	const pills =
		primary.length > 0
			? primary
			: options.filter((option) => option.id !== 'custom').slice(0, PRIMARY_PILL_COUNT);
	const pillIds = new Set(pills.map((option) => option.id));
	const more = options
		.filter((option) => !pillIds.has(option.id))
		.map((option, index) => ({ option, index }))
		// Stable: within a group the page's own order is kept.
		.sort((a, b) => moreGroupOf(a.option.id) - moreGroupOf(b.option.id) || a.index - b.index)
		.map(({ option }) => option);
	return { pills, more };
}

/** Links whose range was already applied, so a re-render or remount does not undo the person's own pick. */
const appliedLinkRanges = new Set<string>();

export function DateRangePillSelector({
	selectedValue,
	onSelect,
	onCustomRangeClick,
	currentRange,
	customOptions,
	className,
	pillClassName,
	disabled = false,
	label = 'Date Range',
	hideCustomOption = false,
	variant = 'pills',
}: DateRangePillSelectorProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const fiscalYearStartMonth = useFiscalYearStartMonth();

	// PD-15754 — a tenant whose fiscal year does not start in January also gets
	// This/Last fiscal quarter and year, just before Custom. Reports resolve the
	// dates through the shared `getDateRanges`, which carries the same ids.
	const defaultOptions: Array<DateRangeOption> = [
		...DATE_RANGE_OPTIONS.filter((option) => option.id !== 'custom'),
		...getFiscalDateRangeOptions(fiscalYearStartMonth).map((option) => ({
			id: option.value,
			label: option.label,
			value: { startDate: null, endDate: null },
		})),
		...DATE_RANGE_OPTIONS.filter((option) => option.id === 'custom'),
	];

	// Use custom options if provided, otherwise use default
	const baseOptions = customOptions
		? [
				...customOptions.map((opt) => ({
					id: opt.value,
					label: opt.label,
					value: { startDate: null, endDate: null },
				})),
				// Add custom range option at the end unless hidden
				...(!hideCustomOption
					? [
							{
								id: 'custom',
								label: 'Custom',
								value: {
									startDate: null as Date | null,
									endDate: null as Date | null,
								},
							},
						]
					: []),
			]
		: defaultOptions;

	const options = hideCustomOption
		? baseOptions.filter((opt) => opt.id !== 'custom')
		: baseOptions;

	// PD-17455 — a link from Share → Copy link names the date range: applied
	// once when the report opens, then the person's own picks take over.
	const linkRange = typeof window === 'undefined' ? null : rangeFromReportLink(window.location.search);
	const linkRangeOffered = linkRange != null && options.some((option) => option.id === linkRange);
	useEffect(() => {
		// A custom range needs its dates, which the page reads from the link itself (useReportDateRangeState).
		if (linkRange == null || linkRange === 'custom' || !linkRangeOffered || disabled) {
			return;
		}
		const key = `${window.location.pathname}${window.location.search}`;
		if (appliedLinkRanges.has(key)) {
			return;
		}
		appliedLinkRanges.add(key);
		if (linkRange !== selectedValue) {
			onSelect(linkRange);
		}
		// Once per opened link (onSelect and selectedValue are left out on purpose); later changes are the person's.
	}, [linkRange, linkRangeOffered]);

	// PD-17455 — what is on screen, for the report header's Share.
	const selectedLabel = options.find((option) => option.id === selectedValue)?.label ?? currentRange.label;
	useEffect(() => {
		reportDateRangeStore.set({
			id: selectedValue,
			label: selectedLabel,
			startDate: currentRange.startDate !== '' ? currentRange.startDate.slice(0, 10) : null,
			endDate: currentRange.endDate !== '' ? currentRange.endDate.slice(0, 10) : null,
		});
	}, [selectedValue, selectedLabel, currentRange.startDate, currentRange.endDate]);
	useEffect(() => () => reportDateRangeStore.set(null), []);

	const { pills, more } = splitDateRangeOptions(options);
	const selectedInMore = more.find((option) => option.id === selectedValue) ?? null;
	const pillClass = (isSelected: boolean): string =>
		cn(
			'px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors',
			'border border-muted',
			isSelected
				? 'bg-accent-badge text-accent-tint-fg border-accent-tint'
				: 'bg-sunken text-muted hover:bg-accent-badge hover:border-accent-tint',
			disabled && 'opacity-50 cursor-not-allowed',
			pillClassName,
		);

	const handlePillClick = (rangeId: string) => {
		if (disabled) {
			return;
		}

		if (rangeId === 'custom') {
			onCustomRangeClick?.();
		} else {
			onSelect(rangeId);
		}
	};

	if (variant === 'select') {
		return (
			// PD-15336 — the select needs a floor width. On a report whose
			// filters share the header row the control otherwise collapses to
			// whatever space is left and every option truncates to "Last …",
			// making Last 7 days, Last 30 days and Last month indistinguishable.
			// 11rem clears the longest label ("Last 12 months") at the xs size;
			// `w-full` keeps it filling a narrower parent rather than overflowing.
			<div className={cn('mb-4 min-w-[11rem]', className)}>
				{label != null && (
					<Text
						className={cn(
							'block font-medium text-xs mb-1',
							colors.classes.textPrimary,
						)}
					>
						{t(label)}
					</Text>
				)}
				<Select
					options={options.map((option) => ({
						value: option.id,
						label: t(option.label),
					}))}
					value={selectedValue}
					onChange={handlePillClick}
					disabled={disabled}
					size="xs"
				/>
				{selectedValue === 'custom' &&
					currentRange.startDate !== '' &&
					currentRange.endDate !== '' && (
						<Text
							size="xs"
							className={cn('block mt-1', colors.classes.textSecondary)}
						>
							<Icon
								icon={faCalendar}
								className="mr-1 inline-block h-2.5 w-2.5"
							/>
							{`${currentRange.startDate} – ${currentRange.endDate}`}
						</Text>
					)}
			</div>
		);
	}

	return (
		<div className={cn('mb-4', className)}>
			{label != null && (
				<Text
					className={cn(
						'block font-medium text-xs mb-1',
						colors.classes.textPrimary,
					)}
				>
					{t(label)}
				</Text>
			)}
			{/* min-h matches the xs form control (32px) with the pills centered
			    inside, and the mb-1 label gap matches FormField's space-y-1 — so
			    when the pills share a header row with labelled filter controls,
			    the labels and the text lines align instead of drifting apart. */}
			<div className="flex flex-wrap gap-1.5 min-h-[32px] items-center">
				{pills.map((option) => (
					<button
						key={option.id}
						type="button"
						onClick={() => {
							handlePillClick(option.id);
						}}
						disabled={disabled}
						className={pillClass(selectedValue === option.id)}
					>
						{t(option.label)}
					</button>
				))}
				{more.length > 0 && (
					<Dropdown
						floating
						trigger={
							<button
								type="button"
								disabled={disabled}
								className={cn(pillClass(selectedInMore != null), 'inline-flex items-center gap-1')}
							>
								{selectedInMore != null ? t(selectedInMore.label) : t('More')}
								{selectedValue === 'custom' && (
									<Icon icon={faCalendar} className="inline-block h-2.5 w-2.5" />
								)}
								<Icon icon={faChevronDown} className="inline-block h-2 w-2" />
							</button>
						}
						items={more.map((option, index) => ({
							label: option.id === 'custom' ? t('Custom...') : t(option.label),
							onClick: () => handlePillClick(option.id),
							dividerBefore: index > 0 && moreGroupOf(option.id) !== moreGroupOf(more[index - 1]?.id ?? ''),
						}))}
					/>
				)}
			</div>
		</div>
	);
}

// Helper function to get date range by ID
export function getDateRangeById(id: string): DateRangeOption | undefined {
	return DATE_RANGE_OPTIONS.find((option) => option.id === id);
}

// Helper function to format date range for display
export function formatDateRange(
	startDate: Date | null,
	endDate: Date | null,
): string {
	if (!startDate || !endDate) {
		return 'Select dates';
	}

	const formatDate = (date: Date) =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});

	if (startDate.toDateString() === endDate.toDateString()) {
		return formatDate(startDate);
	}

	return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}
